import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UsersService } from '../../../services/users/users.service';
import { User } from '../../../state/auth/types';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { PasswordPolicyService } from '../../../services/password-policy.service';
import { first, of, switchMap } from 'rxjs';
import { ConfigurationsService } from '../../../services/configurations.service';

interface Alert {
  type: string;
  message: string;
  invalidFields?: string[];
}

@Component({
  selector: 'lib-create-user-modal',
  templateUrl: './create-user-modal.component.html',
  styleUrls: ['./create-user-modal.component.css'],
  standalone: false
})
export class CreateUserModalComponent implements OnInit {
  userForm: FormGroup;
  alertMessage: Alert;
  loading = false;

  @Input()
  userToCopyFrom: User;
  @Input()
  createUserFrom?: User; // used in account request create user

  allowSendEmailToUser = this.usersService.allowSendEmailToUser; // Only for fortnox for now
  sendEmail = this.usersService.sendEmailToUserByDefault;

  password: string;
  passwordRequirements: any;
  defaultConfigurations: {
    defaultActiveUserGroups: { [role: string]: boolean } | null;
    defaultAssortment: string;
    defaultMultistockIds: string[];
  };

  constructor(
    protected formBuilder: FormBuilder,
    public activeModal: NgbActiveModal,
    protected usersService: UsersService,
    protected backendFeatureService: BackendFeatureService,
    protected passwordPolicyService: PasswordPolicyService,
    protected configurationsService: ConfigurationsService
  ) {}

  ngOnInit() {
    let managedCompanyIdsInput = '';
    let assortmentInput = '';
    const featureId = 'passwordPolicies';
    this.backendFeatureService
      .isActivated(featureId)
      .pipe(
        first(),
        switchMap(isActivated => {
          if (isActivated) {
            return this.passwordPolicyService.getPasswordRequirements();
          } else {
            return of({});
          }
        })
      )
      .subscribe(requirements => {
        this.passwordRequirements = requirements;
      });
    if (!!this.userToCopyFrom) {
      assortmentInput = this.userToCopyFrom.assortment;
      if (this.userToCopyFrom.managedCompanyIds.length > 0) {
        managedCompanyIdsInput = this.userToCopyFrom.managedCompanyIds[0];
      }
      this.initForm(assortmentInput, managedCompanyIdsInput);
    } else {
      this.configurationsService
        .getConfigurationsByIds(['NewUserDefaultsConfig'])
        .pipe(first())
        .subscribe(newUserConfig => {
          if (newUserConfig) {
            this.defaultConfigurations = {
               defaultActiveUserGroups: Array.isArray(newUserConfig[0]?.defaultActiveUserGroups) && newUserConfig[0].defaultActiveUserGroups.length > 0
              ? Object.fromEntries(newUserConfig[0].defaultActiveUserGroups.map(role => [role, true]))
              : null,
              defaultAssortment: newUserConfig[0]?.defaultAssortment,
              defaultMultistockIds: newUserConfig[0]?.defaultMultistockIds
            };
          }
          this.initForm(this.defaultConfigurations?.defaultAssortment || assortmentInput, managedCompanyIdsInput);
        });
    }
    
  }

  initForm(assortmentInput: string, managedCompanyIdsInput: string) {
    this.userForm = this.formBuilder.group({
      username: this.formBuilder.control((this.createUserFrom && this.createUserFrom.username) || '', [
        Validators.required
      ]),
      password: this.formBuilder.control(''),
      name: this.formBuilder.control((this.createUserFrom && this.createUserFrom.name) || '', []),
      email: this.formBuilder.control(this.createUserFrom?.email || '', [Validators.required, Validators.email]),
      phone: this.formBuilder.control(this.createUserFrom?.phone || this.userToCopyFrom?.phone || '', []),
      managedCompanyIds: this.formBuilder.control(
        (this.createUserFrom && this.createUserFrom.managedCompanyIds) || managedCompanyIdsInput
      ),
      assortment: this.formBuilder.control(assortmentInput, [Validators.required])
    });
  }

  onAdd() {
    if (!this.userForm.valid) {
      const invalidFields = [];
      for (const key of Object.keys(this.userForm.controls)) {
        const field = this.userForm.controls[key];
        if (!field.valid) {
          invalidFields.push(key.toUpperCase());
        }
      }
      this.alertMessage = {
        type: 'danger',
        message: 'FORM_CONTAINS_ERROR',
        invalidFields
      };
      return;
    }

    this.loading = true;

    if (this.userToCopyFrom) {
      const newExtraCopyFrom: { [s: string]: any } = {};
      const keysExclude: string[] = [
        'latestLogin',
        'loginData',
        '_successLatestLoginDays',
        '_failedLatestLoginDays',
        '_isCurrentlyLoggedIn',
        'createdAt'
      ];
      for (const key of Object.keys(this.userToCopyFrom?.extra)) {
        if (!keysExclude.includes(key)) {
          newExtraCopyFrom[key] = this.userToCopyFrom.extra[key];
        }
      }

      this.userToCopyFrom = {
        ...this.userToCopyFrom,
        extra: newExtraCopyFrom
      };
    }

    let userForm: User;
    if (!!this.userToCopyFrom) {
      userForm = {
        ...this.userToCopyFrom,
        ...this.userForm.getRawValue(),
        managedCompanyIds: [this.userForm.getRawValue().managedCompanyIds]
      };
    } else {
      userForm = {
        ...this.userForm.getRawValue(),
        activeGroups: this.defaultConfigurations?.defaultActiveUserGroups || {},
        managedCompanyIds: [this.userForm.getRawValue().managedCompanyIds]
      };
    }

    this.usersService.createUser(userForm).subscribe(
      user => {
        this.loading = false;
        this.userForm.reset();
        this.activeModal.close(userForm);
        if (!!this.sendEmail) {
          const domain = location.origin;
          this.usersService.sendWelcomeUserMessage(user.username, domain).subscribe();
        }
      },
      error => {
        this.loading = false;
        this.alertMessage = {
          type: 'danger',
          message: error.error.message
        };
      }
    );
  }

  isValid(event) {
    if (!!this.password && event) {
      this.userForm.get('password').setValue(this.password);
    }
  }
}
